import { styled, Text, useClipboard } from '@obvio/app'
import { SvgCloseMenu, SvgFacebook, SvgMail, SvgTwitter } from '@obvio/svg'
import { Button, Dropdown, Stack } from '@obvio/ui'
import { useState } from 'react'

import { useCursorContext } from './Cursor'

import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

const BlackDropdown = styled(Dropdown)`
  background-color: #000000;
  padding: ${(theme) => `${theme.spacing.extraSmall} ${theme.spacing.medium}`};
  * {
    color: white;
  }
`

const DropdownBody = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #000000;
  min-width: 10rem;
`

const TextButton = styled(Button)`
  min-width: 0;
  padding: 0;
  height: 3rem;
  text-transform: initial;
  > div {
    align-items: center;
  }
`

const ExitButton = styled(Button)`
  position: absolute;
  top: ${(theme) => theme.spacing.extraSmall};
  right: ${(theme) => theme.spacing.medium};
  svg > * {
    stroke-width: 1px;
  }
`

type ShareDropdownProps = AllowUndefinedForOptional<{
  url?: string
  children: ReactElement
}>

export function ShareDropdown({
  children,
  url,
}: ShareDropdownProps): ReactElement {
  const [copied, setCopied] = useState(false)
  const copy = useClipboard(() => setCopied(true))
  const { setKind } = useCursorContext()

  return (
    <BlackDropdown
      onClose={() => {
        setCopied(false)
        setKind('black')
      }}
      render={({ close }) => (
        <DropdownBody
          onMouseOver={() => setKind('white')}
          onMouseOut={() => setKind('black')}
        >
          {copied ? null : (
            <Text tag="span" as="sub-h1">
              Share
            </Text>
          )}
          <ExitButton kind="icon" onClick={close}>
            <SvgCloseMenu strokeWidth={1} />
          </ExitButton>
          {copied ? (
            <Text tag="span" as="sub-h1">
              Copied!
            </Text>
          ) : (
            <Stack kind="vertical" divider spacing="0.1rem">
              <TextButton
                kind="transparent"
                onClick={() => copy(url || window.location.href)}
              >
                COPY LINK
              </TextButton>
              <Stack spacing="medium">
                <TextButton
                  kind="transparent"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${
                    url || window.location.href
                  }`}
                >
                  <Stack align="center">
                    <SvgFacebook />
                    <Text tag="span">Facebook</Text>
                  </Stack>
                </TextButton>
                <Stack>
                  <TextButton
                    kind="transparent"
                    href={`mailto:?subject=Check out this page&body=${
                      url || window.location.href
                    }`}
                  >
                    <Stack align="center">
                      <SvgMail />
                      <Text tag="span">Mail</Text>
                    </Stack>
                  </TextButton>
                </Stack>
                <Stack>
                  <TextButton
                    kind="transparent"
                    href={`https://twitter.com/intent/tweet?text=Check out this page&url=${
                      url || window.location.href
                    }`}
                  >
                    <Stack align="center">
                      <SvgTwitter />
                      <Text tag="span">Twitter</Text>
                    </Stack>
                  </TextButton>
                </Stack>
              </Stack>
            </Stack>
          )}
        </DropdownBody>
      )}
    >
      {children}
    </BlackDropdown>
  )
}
